import { useEffect, useState } from "react";

import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import useMantineMediaQueries from "@hl/base-components/lib/hooks/useMantineMediaQueries";
import {
  OUTLINE_COLOR,
  SHADOW_COLOR,
} from "@hl/base-components/lib/theme/button";
import {
  BACKGROUND_COLOR,
  STATUS_COLOR,
  TEXT_COLOR,
  UTILITY_COLOR,
} from "@hl/base-components/lib/theme/colors";
import { NoResults } from "@hl/shared-features/lib/features/collections/NoResults";
import DescriptionMarkdown from "@hl/shared-features/lib/features/markdown/DescriptionMarkdown";
import {
  Anchor,
  Badge,
  Box,
  Button,
  Grid,
  Group,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";

import {
  _CollectionType,
  NftContractStandard,
} from "../../apollo/graphql.generated";
import { getMintPageUrl, TOOLS_URL } from "../../config";

import { CollectionMedia } from "./CollectionMedia";
import {
  GetPublicCollectionByWalletAddressQuery,
  useGetPublicCollectionByWalletAddressQuery,
} from "./queries/profile.graphql.generated";

type PublicCollections =
  GetPublicCollectionByWalletAddressQuery["getPublicCollectionsByWallet"]["collections"];

const UserCollections = ({
  slug,
  isOwner,
  onLoad,
  active,
}: {
  slug: string;
  isOwner: boolean;
  onLoad?: (count: number) => void;
  active?: boolean;
}) => {
  const [cursor, setCursor] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const { isMobile, isLarge } = useMantineMediaQueries();
  const theme = useMantineTheme();

  const { data, loading, error } = useGetPublicCollectionByWalletAddressQuery({
    variables: {
      walletAddress: slug,
      cursor,
      hidden: false,
    },
    skip: !slug,
  });
  const [collections, setCollections] = useState<PublicCollections | null>(
    null
  );

  useEffect(() => {
    if (loading) return;
    if (!data) return;

    const newCollections = data.getPublicCollectionsByWallet.collections;
    onLoad?.(data.getPublicCollectionsByWallet.pageInfo.totalCount);
    setCollections((collections ?? []).concat(newCollections));
    setNextCursor(data.getPublicCollectionsByWallet.pageInfo.endCursor);
  }, [data, loading]);

  if (!active) return null;
  if (loading && !cursor) return <LoadingPage />;

  if (collections && collections.length === 0 && isOwner)
    return (
      <NoResults
        title="You haven’t created any collections on Highlight"
        description="Once you do, they’ll appear here."
        label="Create collection"
        link={TOOLS_URL.base}
      />
    );

  if (error) {
    return (
      <Text mt="sm" size="sm" align="center" color={STATUS_COLOR.ERROR}>
        Error loading collections
      </Text>
    );
  }

  return (
    <Stack align="center">
      {collections?.map((collection) => (
        <Stack
          mb={100}
          spacing={32}
          align="center"
          key={collection.id}
          w="100%"
        >
          <Anchor w="100%" href={getMintPageUrl(collection)} target="_blank">
            <Box
              bg={UTILITY_COLOR.TABLE_ROW_BACKGROUND}
              p={{ base: 0, md: 80 }}
            >
              <CollectionMedia
                height={isMobile ? "200px" : "440px"}
                width={isMobile ? "100%" : "auto"}
                imageUrl={
                  collection.editions?.length
                    ? collection.editions[0].image
                    : collection.seriesDetails?.logoUrl ||
                      collection.generativeDetails?.logoUrl ||
                      collection.importData?.logoUrl
                }
                animationUrl={
                  collection.editions?.length
                    ? collection.editions[0].animation
                    : null
                }
              />
            </Box>
          </Anchor>
          <Grid justify="center" w="100%" gutter={isLarge ? 80 : 32}>
            <Grid.Col lg={5} md={12} sm={12} pr={{ base: 0, lg: 80 }} pl={0}>
              <Title
                order={2}
                sx={{
                  wordWrap: "break-word",
                }}
              >
                {collection.name}
              </Title>
              <Group mt={8}>
                <Text size="sm" color={TEXT_COLOR.PLACEHOLDER}>
                  {collectionSubtitle(collection)}
                </Text>
                <Badge
                  color={BACKGROUND_COLOR.QUATERNARY}
                  size="xs"
                  styles={{
                    root: {
                      borderRadius: "999px",
                      height: "24px",
                      padding: "4px 4px",
                      backgroundColor: theme.colors.baseBackground[0],
                      outline: `1px solid ${theme.colors.divider[0]}`,
                    },
                  }}
                >
                  <Text size="xs" color={TEXT_COLOR.PRIMARY} tt="lowercase">
                    {collection.standard === NftContractStandard.ERC1155
                      ? "ERC-1155"
                      : "ERC-721"}
                  </Text>
                </Badge>
              </Group>
            </Grid.Col>
            <Grid.Col lg={7} md={12} sm={12} px={{ base: 0, lg: 80 }}>
              <Stack spacing={20}>
                <DescriptionMarkdown
                  hasExpand
                  description={
                    collection.description ||
                    (collection.editions?.length
                      ? collection.editions[0]?.description
                      : "")
                  }
                  noPopover
                  lineClamp={isLarge ? 6 : 10}
                  numChars={300}
                  hasLineClampExpanded={false}
                  mb={8}
                  enableExpandOnClickText={false}
                />
                <Button
                  w={140}
                  size="md"
                  component="a"
                  href={getMintPageUrl(collection)}
                  target="_blank"
                  color={SHADOW_COLOR}
                >
                  View project
                </Button>
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      ))}
      {collections &&
        (data?.getPublicCollectionsByWallet.pageInfo.hasNextPage ||
          loading) && (
          <Button
            mt={-40}
            size="lg"
            color={OUTLINE_COLOR}
            onClick={() => setCursor(nextCursor)}
            loading={loading}
          >
            Load more
          </Button>
        )}
    </Stack>
  );
};

const collectionSubtitle = (collection: PublicCollections[0]) => {
  if (collection.collectionType === _CollectionType.OneOfOne) {
    return "One of One";
  }

  if (collection.collectionType === _CollectionType.Series) {
    return "Series";
  }
  if (collection.collectionType === _CollectionType.GenerativeSeries) {
    return "Generative series";
  }

  if (collection.collectionType === _CollectionType.LimitedEdition) {
    const size = collection.size;
    return size ? `Edition of ${size}` : "Limited Edition";
  }

  if (collection.collectionType === _CollectionType.OpenEdition) {
    return "Open Edition";
  }
};

export default UserCollections;
